import { apiFetch } from "./apiFetch";

export interface VideoPlatformMeeting {
  id: string;
  externalMeetingId: string;
  title: string;
  description: string;
  status: string;
  type: string;
  createdBy: string;
  hostId: string;
  createdAt: string;
  meetingCreatedAt: string;
  startedAt: string;
  endedAt: string;
  duration: number;
  durationMinutes: number;
  originalMeetingId: string;
  versionNumber: number;
  category?: string;
  isPublicVisible?: boolean;
}

export interface MeetingCategoryOption {
  id: string;
  title: string;
}

export interface CapabilityControl {
  value: string;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: string;
}

export interface MeetingAttendee {
  id: string;
  externalMeetingId: string;
  attendeeId: string;
  userId: string;
  externalUserId: string;
  attendeeName: string;
  joinedAt: string;
  leftAt: string;
  isActive: boolean;
  capabilities: {
    audio: CapabilityControl;
    video: CapabilityControl;
    content: CapabilityControl;
  };
}

export interface Paging {
  count: number;
  total?: number;
  limit: number;
  nextToken?: string | null;
}

export interface MeetingsListResponse {
  data: VideoPlatformMeeting[];
  paging: Paging;
  filters?: Record<string, any>;
}

export interface AttendeesListResponse {
  data: MeetingAttendee[];
  paging: Paging;
}

export const VideoPlatformResource = {
  getMeetings: (params?: {
    status?: string;
    createdBy?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    nextToken?: string;
    prioritizeStatus?: string;
    sortBy?: string;
    sortOrder?: string;
  }) =>
    apiFetch<MeetingsListResponse>("/admin/video-platform/meetings", {
      searchParams: params,
    }),

  getMeeting: (id: string) =>
    apiFetch<VideoPlatformMeeting>(`/admin/video-platform/meetings/${id}`),

  createMeeting: (data: Record<string, any>) =>
    apiFetch<VideoPlatformMeeting>("/admin/video-platform/meetings", {
      method: "POST",
      body: data,
    }),

  deleteMeeting: (id: string) =>
    apiFetch<VideoPlatformMeeting>(`/admin/video-platform/meetings/${id}`, {
      method: "DELETE",
    }),

  getMeetingAttendees: (params: {
    meetingId: string;
    active?: boolean;
    limit?: number;
    nextToken?: string;
  }) => {
    const { meetingId, ...searchParams } = params;
    return apiFetch<AttendeesListResponse>(
      `/admin/video-platform/meetings/${meetingId}/attendees`,
      { searchParams }
    );
  },

  updateAttendeeCapabilities: (
    meetingId: string,
    attendeeId: string,
    capabilities: {
      audio?: { value?: string; lock?: boolean };
      video?: { value?: string; lock?: boolean };
      content?: { value?: string; lock?: boolean };
    }
  ) =>
    apiFetch<{
      attendeeId: string;
      userId: string;
      audioCapability: CapabilityControl;
      videoCapability: CapabilityControl;
      contentCapability: CapabilityControl;
    }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/${attendeeId}/capabilities`,
      { method: "PATCH", body: capabilities }
    ),

  batchUpdateAttendeeCapabilities: (
    meetingId: string,
    body: {
      operation:
        | "mute_all"
        | "unmute_all"
        | "disable_video_all"
        | "enable_video_all"
        | "disable_content_all"
        | "enable_content_all";
      excludeHosts: boolean;
      lock: boolean;
    }
  ) =>
    apiFetch<{ updatedCount: number; attendeeIds: string[] }>(
      `/admin/video-platform/meetings/${meetingId}/attendees/batch`,
      { method: "PATCH", body }
    ),

  getCategories: () =>
    apiFetch<{ categories: MeetingCategoryOption[] }>(
      "/admin/video-platform/meetings/categories"
    ),

  updateMeeting: (
    id: string,
    data: {
      category?: string;
      title?: string;
      description?: string;
      isPublicVisible?: boolean;
    }
  ) =>
    apiFetch<{
      id: string;
      externalMeetingId: string;
      title: string;
      category?: string;
      status: string;
      isPublicVisible?: boolean;
    }>(`/admin/video-platform/meetings/${id}`, {
      method: "PATCH",
      body: data,
    }),
};
